import { useState } from "react";
import { saveAs } from "@progress/kendo-file-saver";
import * as XLSX from "xlsx";
import type { TableInvoiceRow } from "../pages/debitoren/types/invoice-table.types";
import type { PaymentJournalRow } from "../pages/debitoren/types/payment-journal.types";
import {
	prepareExportData,
	preparePaymentJournalExportData,
	convertToCSV,
	generateExportFilename,
	ExportOptions,
	calculateExportSummary,
	createSummaryRows,
	toLabeledExportRows,
	ExportHeaderMetadata,
	ExportRowData,
	ExportSummary,
} from "../lib/utils/export-utils";
import {
	createInvoicePdfZip,
	InvoicePdfProgress,
	InvoicePdfZipSummary,
} from "../lib/utils/invoice-pdf-zip";
import { useExportActions } from "./actions/useExportActions";
import { Logger } from "../../../lib/logger/Logger";

export type ExportData = TableInvoiceRow[] | PaymentJournalRow[];

export type ExportFormat = "csv" | "excel" | "pdf";

export interface ExportDataResult {
	data: ExportData;
	totalCount: number;
	error?: string;
}

export interface TableExportOptions extends ExportOptions {
	includeInvoicePdfs?: boolean;
	signal?: AbortSignal;
	onProgress?: (progress: InvoicePdfProgress) => void;
}

interface ExportFile {
	blob: Blob;
	filename: string;
}

interface PreparedExport {
	rows: ExportRowData[];
	summary: ExportSummary;
	isPaymentJournal: boolean;
	hasInvoices: boolean;
}

export type ExportRenderer = (
	prepared: PreparedExport,
	metadata: ExportHeaderMetadata
) => Blob | Promise<Blob>;

const FORMAT_LABELS: Record<ExportFormat, string> = {
	csv: "CSV",
	excel: "Excel",
	pdf: "PDF",
};

const prepareExport = (
	data: ExportData,
	options: ExportOptions,
	metadata: ExportHeaderMetadata
): PreparedExport => {
	const isPaymentJournal = metadata.exportType === "zahlungsjournal";
	const rows = isPaymentJournal
		? preparePaymentJournalExportData(data as PaymentJournalRow[], options)
		: prepareExportData(data as TableInvoiceRow[], options);
	const summary = calculateExportSummary(rows);
	return {
		rows,
		summary,
		isPaymentJournal,
		hasInvoices: summary.invoiceCount > 0,
	};
};

const withSummaryRows = ({
	rows,
	summary,
	isPaymentJournal,
}: PreparedExport) => [
	...rows,
	...createSummaryRows(summary, isPaymentJournal),
];

const renderCsv: ExportRenderer = (prepared) =>
	new Blob(
		[
			convertToCSV(
				withSummaryRows(prepared),
				prepared.isPaymentJournal,
				prepared.hasInvoices
			),
		],
		{ type: "text/csv;charset=utf-8" }
	);

const renderExcel: ExportRenderer = (prepared, metadata) => {
	const worksheet = XLSX.utils.json_to_sheet(
		toLabeledExportRows(
			withSummaryRows(prepared),
			prepared.isPaymentJournal,
			prepared.hasInvoices
		)
	);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, metadata.exportType);
	return new Blob(
		[XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
		{
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}
	);
};

export const buildListFile = async (
	data: ExportData,
	format: ExportFormat,
	options: ExportOptions,
	metadata: ExportHeaderMetadata,
	renderPdf: ExportRenderer
): Promise<ExportFile> => {
	try {
		const prepared = prepareExport(data, options, metadata);
		const render = { csv: renderCsv, excel: renderExcel, pdf: renderPdf }[
			format
		];
		return {
			blob: await render(prepared, metadata),
			filename: generateExportFilename(format, metadata.exportType),
		};
	} catch (error) {
		Logger.error(error, {}, `${FORMAT_LABELS[format]} export failed`);
		throw new Error(`${FORMAT_LABELS[format]} Export fehlgeschlagen`);
	}
};

export const useTableExport = () => {
	const [exporting, setExporting] = useState<string | null>(null);
	const { exportDebitorenExport } = useExportActions();

	const renderPdf: ExportRenderer = async ({ rows, summary }, metadata) => {
		const result = await exportDebitorenExport(rows, summary, metadata);
		if (!result.success || !result.blob) {
			throw new Error(result.error || "PDF generation failed");
		}
		return result.blob;
	};

	const exportTable = async (
		data: ExportData,
		format: ExportFormat,
		options: TableExportOptions = { includePayments: false },
		headerMetadata: ExportHeaderMetadata
	): Promise<InvoicePdfZipSummary | null> => {
		if (data.length === 0) {
			throw new Error("Keine Daten zum Exportieren vorhanden");
		}

		setExporting(format);

		try {
			const listFile = await buildListFile(
				data,
				format,
				options,
				headerMetadata,
				renderPdf
			);

			if (!options.includeInvoicePdfs) {
				options.signal?.throwIfAborted();
				saveAs(listFile.blob, listFile.filename);
				Logger.info(`Table export successful: ${listFile.filename}`, {
					rowCount: data.length,
				});
				return null;
			}

			const { blob, summary } = await createInvoicePdfZip({
				listFile,
				invoices: data as TableInvoiceRow[],
				signal: options.signal,
				onProgress: options.onProgress,
			});
			const filename = generateExportFilename(
				"zip",
				headerMetadata.exportType
			);
			saveAs(blob, filename);
			Logger.info(
				`Table export with invoice PDFs successful: ${filename}`,
				{
					rowCount: data.length,
					...summary,
				}
			);
			return summary;
		} finally {
			setExporting(null);
		}
	};

	return {
		exportTable,
		exporting,
	};
};
